import { type JSX, useState } from 'react';
import { Collaborator } from '../../../../types/collaborator.types';
import { AccountProfile } from '../../../../types/profiles.types';
import { useResponsive } from '../../../../hooks/useResponsive';
import CollaboratorsCards from './CollaboratorsCards';
import CollaboratorsTable from './CollaboratorsTable';

export interface CollaboratorsViewProps {
  collaborators: Collaborator[];
  getProfileLabel: (collaborator: Collaborator) => string;
  setSortValue: (value: string) => boolean;
  setSelectedCollaborator: (collaborator: Collaborator) => void;
  setEditDialogId: (id: string | null) => void;
  setDeleteDialogId: (id: string | null) => void;
}

interface CollaboratorsListProps {
  collaborators: Collaborator[];
  profiles: AccountProfile[];
  setSelectedCollaborator: (collaborator: Collaborator) => void;
  setEditDialogId: (id: string | null) => void;
  setDeleteDialogId: (id: string | null) => void;
}

const CollaboratorsList = ({
  collaborators,
  profiles,
  setSelectedCollaborator,
  setEditDialogId,
  setDeleteDialogId,
}: CollaboratorsListProps): JSX.Element => {
  const { isMobile } = useResponsive();

  // Sorting states
  type SortType = 'ascending' | 'descending';
  type SortValue = 'firstName' | 'lastName' | 'email' | 'profileId';

  const [sortType, setSortType] = useState<SortType>('ascending');
  const [sortValue, setSortValue] = useState<SortValue>('lastName');

  const sortByString = (dataToSort: Collaborator[], sortByValue: SortValue, type: SortType) => {
    const normalize = (val: unknown) =>
      val === undefined || val === null ? '' : String(val).toUpperCase();

    const compareCollaborator = (a: Collaborator, b: Collaborator, key: SortValue) => {
      return compareValue(a[key], b[key]);
    };

    const compareValue = (valueA: string, valueB: string) => {
      const nameA = normalize(valueA);
      const nameB = normalize(valueB);

      if (nameA === nameB) {
        return 0;
      }

      if (type === 'ascending') {
        return nameA < nameB ? -1 : 1;
      }

      return nameA > nameB ? -1 : 1; // descending
    };

    const isNameSort = sortByValue === 'firstName' || sortByValue === 'lastName';
    const isProfileIdSort = sortByValue === 'profileId';

    return [...dataToSort].sort((a, b) => {
      if (isProfileIdSort) {
        const profileA = getProfileLabel(a);
        const profileB = getProfileLabel(b);
        const result = compareValue(profileA, profileB);

        if (result !== 0) {
          return result;
        }

        return compareCollaborator(a, b, 'email');
      }

      const primary = compareCollaborator(a, b, sortByValue);

      if (primary !== 0) {
        return primary;
      }

      if (isNameSort) {
        const secondaryKey: SortValue = sortByValue === 'firstName' ? 'lastName' : 'firstName';
        const secondary = compareCollaborator(a, b, secondaryKey);
        if (secondary !== 0) {
          return secondary;
        }
      }

      return 0;
    });
  };

  const setSortCollaborators = (fieldName: string): boolean => {
    const value = fieldName as SortValue;

    setSortType(prev => {
      if (sortValue !== value) {
        return 'ascending';
      }
      return prev === 'ascending' ? 'descending' : 'ascending';
    });
    setSortValue(value);

    return sortType === 'ascending';
  };

  const getProfileLabel = (collaborator: Collaborator) => {
    return (
      profiles.find(p => p.id === collaborator.profileId)?.name ||
      `[${collaborator.profileLabel}]` ||
      `<${collaborator.profileId}>`
    );
  };

  const getSortedCollaborators = () => {
    return sortByString(collaborators, sortValue, sortType);
  };

  if (isMobile) {
    // Version mobile : cartes au lieu de tableau
    return (
      <CollaboratorsCards
        collaborators={getSortedCollaborators()}
        getProfileLabel={getProfileLabel}
        setSortValue={setSortCollaborators}
        setSelectedCollaborator={setSelectedCollaborator}
        setEditDialogId={setEditDialogId}
        setDeleteDialogId={setDeleteDialogId}
      />
    );
  }

  // Version desktop : tableau normal
  return (
    <CollaboratorsTable
      collaborators={getSortedCollaborators()}
      getProfileLabel={getProfileLabel}
      setSortValue={setSortCollaborators}
      setSelectedCollaborator={setSelectedCollaborator}
      setEditDialogId={setEditDialogId}
      setDeleteDialogId={setDeleteDialogId}
    />
  );
};

export default CollaboratorsList;
